import React, { createContext, useContext, useMemo } from "react"
import PropTypes from "prop-types"
import { useSortable } from "@dnd-kit/sortable"

import styles from "./index.module.css"
import { DSIcon } from "ui/Icon/DSIcon"

export const DragHandleContext = createContext({ listeners: {}, attributes: {} })

/**
 * Whether row dragging is disabled for the whole table (e.g. while a column
 * sort is active). Provided by the table, consumed by each row + handle cell.
 */
export const SortableDisabledContext = createContext(false)

export const DragHandleCell = () => {
  const { listeners, attributes } = useContext(DragHandleContext)
  const disabled = useContext(SortableDisabledContext)
  return (
    <span
      className={disabled ? `${styles.dragHandle} ${styles.dragHandleDisabled}` : styles.dragHandle}
      {...(disabled ? {} : listeners)}
      {...(disabled ? {} : attributes)}
      aria-label={disabled ? "Reordering disabled while sorting" : "Drag to reorder"}
      aria-disabled={disabled}
    >
      <DSIcon name="dragHandle" width={16} height={16} />
    </span>
  )
}

/**
 * Ant Design Table row component override — sortable via @dnd-kit/sortable.
 * Pass as `components={{ body: { row: SortableTableRow } }}` on a Table
 * wrapped in `<DndContext>` + `<SortableContext>`.
 *
 * Visual states:
 *  - isDragging  → row collapses to zero height (DragOverlay ghost floats above)
 *  - isOver      → swap target highlighted with tinted border
 */
const SortableTableRow = ({ children, ...props }) => {
  const id = props["data-row-key"]
  const disabled = useContext(SortableDisabledContext)
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    isOver,
  } = useSortable({ id, disabled })

  const style = {
    ...props.style,
    opacity: isDragging ? 0.4 : 1,
    transition: "opacity 150ms ease",
    ...(isDragging ? { boxShadow: "0 4px 16px rgba(0,0,0,0.12)", background: "#fafafa" } : {}),
  }

  const contextValue = useMemo(() => ({ listeners, attributes }), [listeners, attributes])

  const rowClass = [
    props.className,
    isDragging ? styles.rowDragging : "",
    isOver && !isDragging ? styles.rowSwapTarget : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <DragHandleContext.Provider value={contextValue}>
      <tr ref={setNodeRef} style={style} {...props} className={rowClass}>
        {children}
      </tr>
    </DragHandleContext.Provider>
  )
}

SortableTableRow.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  className: PropTypes.string,
  "data-row-key": PropTypes.string
}

export default SortableTableRow
